import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import TextField from '@mui/material/TextField';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useCustomerProfile from './useCustomerProfile';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    maxHeight: '90vh',
    width: '800px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflow: 'auto',
  },
};

const FutureRateModal = ({ isOpen, onClose, profile, accumulatedOrderAmount }) => {
  const shopName = useSelector((state) => state.shop.name);
  const { updateCustomerRateAdjustment, getCustomerRateAdjustments } = useCustomerProfile();
  const [isLoading, setIsLoading] = useState(false);
  const [existingRateAdjustments, setExistingRateAdjustments] = useState([]);
  const [formDataList, setFormDataList] = useState(
    Array(5).fill(null).map(() => ({
      accumulatedAmount: null,
      newRate: null,
      rateDiscount: null,
      transactionFee: null,
      id: null // Store existing rate adjustment ID for update
    }))
  );

  const isSmileShop = shopName === 'smile';
  const isTamaShop = shopName === 'tama';

  // Helper function to format integer values (remove decimals)
  const formatInteger = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    return Math.floor(num).toString();
  };

  // Helper function to format decimal values (up to 2 decimal places)
  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    // Round to 2 decimal places and remove trailing zeros
    return parseFloat(num.toFixed(2)).toString();
  };

  // Helper function to format decimal values for newRate (up to 4 decimal places)
  const formatDecimal4Places = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = parseFloat(value);
    if (isNaN(num)) return null;
    // Round to 4 decimal places and remove trailing zeros
    return parseFloat(num.toFixed(4)).toString();
  };

  // Helper function to format number with thousand separators (commas)
  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || value === '') return '';
    // Remove existing commas and parse
    const numStr = String(value).replace(/,/g, '');
    if (!numStr || numStr === '0') return numStr;
    if (isNaN(parseFloat(numStr))) return '';
    // Add thousand separators
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to parse number from comma-formatted string
  const parseNumberFromCommas = (value) => {
    if (value === null || value === undefined || value === '') return null;
    // Remove commas and parse
    const numStr = String(value).replace(/,/g, '');
    if (!numStr) return null;
    const num = parseFloat(numStr);
    return isNaN(num) ? null : num;
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setFormDataList(Array(5).fill(null).map(() => ({
        accumulatedAmount: null,
        newRate: null,
        rateDiscount: null,
        transactionFee: null,
        id: null
      })));
      setExistingRateAdjustments([]);
      return;
    }

    if (!profile?.id) return;

    const fetchExistingRates = async () => {
      setIsLoading(true);
      try {
        const result = await getCustomerRateAdjustments(profile.id);
        console.log('Fetched rate adjustments:', result);
        
        // Convert object with numeric keys to array
        let rateAdjustments = [];
        if (Array.isArray(result)) {
          rateAdjustments = result;
        } else if (result && typeof result === 'object') {
          // Handle object with numeric keys (like {0: {...}, 1: {...}, count: 2})
          rateAdjustments = Object.keys(result)
            .filter(key => !isNaN(parseInt(key)) && key !== 'count')
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(key => result[key]);
        }
        
        console.log('Converted rate adjustments array:', rateAdjustments);
        
        if (rateAdjustments.length > 0) {
          setExistingRateAdjustments(rateAdjustments);
          // Populate form with existing data
          const initialData = Array(5).fill(null).map((_, index) => {
            if (index < rateAdjustments.length) {
              const existing = rateAdjustments[index];
              console.log(`Row ${index} existing data:`, existing);
              // Format values: integers for most fields, 2 decimals for rateDiscount, 4 decimals for newRate
              return {
                accumulatedAmount: existing.threshold !== undefined && existing.threshold !== null ? formatInteger(existing.threshold) : null,
                newRate: existing.newRate !== undefined && existing.newRate !== null ? formatDecimal4Places(existing.newRate) : null,
                rateDiscount: existing.extraPercentDiscount !== undefined && existing.extraPercentDiscount !== null ? formatDecimal(existing.extraPercentDiscount) : null,
                transactionFee: existing.transactionFee !== undefined && existing.transactionFee !== null ? formatInteger(existing.transactionFee) : null,
                id: existing.id !== undefined && existing.id !== null ? existing.id : null
              };
            }
            // For rows beyond existing data, set to null
            return {
              accumulatedAmount: null,
              newRate: null,
              rateDiscount: null,
              transactionFee: null,
              id: null
            };
          });
          console.log('Populated form data:', initialData);
          setFormDataList(initialData);
        } else {
          // No existing data, initialize empty
          console.log('No existing data, initializing empty');
          const initialData = Array(5).fill(null).map(() => ({
            accumulatedAmount: null,
            newRate: null,
            rateDiscount: null,
            transactionFee: null,
            id: null
          }));
          setFormDataList(initialData);
        }
      } catch (error) {
        console.error('Error fetching rate adjustments:', error);
        // Initialize empty on error
        const initialData = Array(5).fill(null).map(() => ({
          accumulatedAmount: null,
          newRate: null,
          rateDiscount: null,
          transactionFee: null,
          id: null
        }));
        setFormDataList(initialData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExistingRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profile?.id]);

  const handleInputChange = (index, field, value) => {
    setFormDataList(prev => {
      const newList = [...prev];
      let processedValue = value;
      
      if (value !== '' && value !== null) {
        // For newRate, allow up to 4 decimal places
        if (field === 'newRate') {
          // Remove any characters that aren't digits or decimal point (including commas)
          processedValue = value.replace(/[^\d.]/g, '');
          // Ensure only one decimal point
          const parts = processedValue.split('.');
          if (parts.length > 2) {
            processedValue = parts[0] + '.' + parts.slice(1).join('');
          }
          // Limit to 4 decimal places
          if (parts.length === 2 && parts[1].length > 4) {
            processedValue = parts[0] + '.' + parts[1].substring(0, 4);
          }
        } else if (field === 'rateDiscount') {
          // For rateDiscount, allow up to 2 decimal places
          // Remove any characters that aren't digits or decimal point (including commas)
          processedValue = value.replace(/[^\d.]/g, '');
          // Ensure only one decimal point
          const parts = processedValue.split('.');
          if (parts.length > 2) {
            processedValue = parts[0] + '.' + parts.slice(1).join('');
          }
          // Limit to 2 decimal places
          if (parts.length === 2 && parts[1].length > 2) {
            processedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        } else {
          // For integer fields (accumulatedAmount, transactionFee), remove non-digits (including commas)
          // We'll store the raw number without commas, but display with commas
          processedValue = value.replace(/[^\d]/g, '');
        }
      }
      
      newList[index] = {
        ...newList[index],
        [field]: processedValue === '' ? null : processedValue
        // id field is preserved automatically
      };
      return newList;
    });
  };

  const handleSave = async () => {
    const customerId = profile?.id;
    if (!customerId) {
      toast.error('ไม่พบข้อมูลลูกค้า');
      return;
    }

    const currentAccumulatedAmount = parseFloat(accumulatedOrderAmount) || 0;
    const rowsWithData = formDataList
      .map((row, index) => ({ row, index: index + 1 }))
      .filter(({ row }) => row.accumulatedAmount != null || row.newRate != null || row.rateDiscount != null || row.transactionFee != null);
    
    for (const { row, index } of rowsWithData) {
      // Validate required fields
      if (isSmileShop && (row.accumulatedAmount == null || row.newRate == null)) {
        toast.error(`แถวที่ ${index}: กรุณากรอกข้อมูลให้ครบถ้วน (ยอดสะสม และ เรทใหม่)`);
        return;
      }
      if (isTamaShop && (row.accumulatedAmount == null || row.rateDiscount == null || row.transactionFee == null)) {
        toast.error(`แถวที่ ${index}: กรุณากรอกข้อมูลให้ครบถ้วน (ยอดสะสม, ส่วนลดเรท, และ ค่าบริการสั่งซื้อ)`);
        return;
      }
      
      // Validate accumulatedAmount
      if (parseFloat(row.accumulatedAmount) < currentAccumulatedAmount) {
        toast.error(`แถวที่ ${index}: ยอดสะสมต้องไม่ต่ำกว่ายอดสะสมปัจจุบัน (${currentAccumulatedAmount})`);
        return;
      }
      
      // Validate newRate (must be between 0.2 and 0.3)
      if (isSmileShop && row.newRate != null) {
        const newRateValue = parseFloat(row.newRate);
        if (isNaN(newRateValue) || newRateValue < 0.2 || newRateValue > 0.3) {
          toast.error(`แถวที่ ${index}: เรทใหม่ต้องอยู่ระหว่าง 0.2 ถึง 0.3`);
          return;
        }
      }
      
      // Validate rateDiscount (must be between 0 and 6)
      if (isTamaShop && row.rateDiscount != null) {
        const rateDiscountValue = parseFloat(row.rateDiscount);
        if (isNaN(rateDiscountValue) || rateDiscountValue < 0 || rateDiscountValue > 6) {
          toast.error(`แถวที่ ${index}: ส่วนลดเรทต้องอยู่ระหว่าง 0 ถึง 6`);
          return;
        }
      }
    }
    
    // Validate that higher thresholds have better benefits
    // Sort rows by threshold (ascending) to compare them
    const sortedRowsWithData = [...rowsWithData].sort((a, b) => {
      const thresholdA = parseFloat(a.row.accumulatedAmount) || 0;
      const thresholdB = parseFloat(b.row.accumulatedAmount) || 0;
      return thresholdA - thresholdB;
    });
    
    for (let i = 1; i < sortedRowsWithData.length; i++) {
      const current = sortedRowsWithData[i];
      const previous = sortedRowsWithData[i - 1];
      const currentThreshold = parseFloat(current.row.accumulatedAmount);
      const previousThreshold = parseFloat(previous.row.accumulatedAmount);
      
      // Only validate if current threshold is higher than previous
      if (currentThreshold > previousThreshold) {
        if (isSmileShop) {
          // newRate cannot be higher than previous (should be lower or equal for better benefit)
          const currentNewRate = parseFloat(current.row.newRate);
          const previousNewRate = parseFloat(previous.row.newRate);
          if (!isNaN(currentNewRate) && !isNaN(previousNewRate) && currentNewRate > previousNewRate) {
            toast.error(`แถวที่ ${current.index}: เนื่องจากยอดสะสมสูงกว่าแถวก่อนหน้า เรทใหม่ต้องไม่สูงกว่าแถวก่อนหน้า (${previousNewRate})`);
            return;
          }
        } else if (isTamaShop) {
          // extraPercentDiscount cannot be lower than previous (should be higher or equal for better benefit)
          const currentRateDiscount = parseFloat(current.row.rateDiscount);
          const previousRateDiscount = parseFloat(previous.row.rateDiscount);
          if (!isNaN(currentRateDiscount) && !isNaN(previousRateDiscount) && currentRateDiscount < previousRateDiscount) {
            toast.error(`แถวที่ ${current.index}: เนื่องจากยอดสะสมสูงกว่าแถวก่อนหน้า ส่วนลดเรทต้องไม่ต่ำกว่าแถวก่อนหน้า (${previousRateDiscount})`);
            return;
          }
          
          // transactionFee cannot be higher than previous (should be lower or equal for better benefit)
          const currentTransactionFee = parseFloat(current.row.transactionFee);
          const previousTransactionFee = parseFloat(previous.row.transactionFee);
          if (!isNaN(currentTransactionFee) && !isNaN(previousTransactionFee) && currentTransactionFee > previousTransactionFee) {
            toast.error(`แถวที่ ${current.index}: เนื่องจากยอดสะสมสูงกว่าแถวก่อนหน้า ค่าบริการสั่งซื้อต้องไม่สูงกว่าแถวก่อนหน้า (${previousTransactionFee})`);
            return;
          }
        }
      }
    }
    
    // Map fields to backend format for bulk update
    const rateAdjustments = rowsWithData.map(({ row }) => {
      const baseData = {
        threshold: parseFloat(row.accumulatedAmount),
        id: row.id || null // Include ID if exists (for update)
      };
      
      if (isSmileShop) {
        return {
          ...baseData,
          newRate: parseFloat(row.newRate),
          extraPercentDiscount: null,
          transactionFee: null
        };
      } else {
        return {
          ...baseData,
          newRate: null,
          extraPercentDiscount: parseFloat(row.rateDiscount),
          transactionFee: parseFloat(row.transactionFee)
        };
      }
    });
    
    setIsLoading(true);
    try {
      // Send bulk update/create request
      const bulkData = {
        customerId: customerId,
        rateAdjustments: rateAdjustments
      };
      console.log(bulkData)  
      const result = await updateCustomerRateAdjustment(bulkData);
    
      if (result !== true) {
        toast.error(result || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล');
        setIsLoading(false);
        return;
      }
      
      toast.success('บันทึกข้อมูลสำเร็จ');
      onClose();
    } catch (error) {
      toast.error('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {}} // Prevent closing on outside click
      style={modalStyles}
      ariaHideApp={false}
      shouldCloseOnOverlayClick={false}
    >
      <div className="p-4">
        <h2 className="text-2xl font-bold mb-4">ปรับเรท อนาคต</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">ยอดสะสม</th>
                {isSmileShop && (
                  <th className="border border-gray-300 px-4 py-2 text-left">เรทใหม่ (smile)</th>
                )}
                {isTamaShop && (
                  <>
                    <th className="border border-gray-300 px-4 py-2 text-left">ส่วนลดเรท (tama)</th>
                    <th className="border border-gray-300 px-4 py-2 text-left">ค่าบริการสั่งซื้อ (tama)</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {formDataList.map((formData, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 px-4 py-2">
                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      value={formatNumberWithCommas(formData.accumulatedAmount)}
                      onChange={(e) => handleInputChange(index, 'accumulatedAmount', e.target.value)}
                      variant="outlined"
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9,]*' }}
                    />
                  </td>
                  {isSmileShop && (
                    <td className="border border-gray-300 px-4 py-2">
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={formData.newRate ?? ''}
                        onChange={(e) => handleInputChange(index, 'newRate', e.target.value)}
                        inputProps={{ step: 0.0001, min: 0.2, max: 0.3 }}
                        variant="outlined"
                      />
                    </td>
                  )}
                  {isTamaShop && (
                    <>
                      <td className="border border-gray-300 px-4 py-2">
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={formData.rateDiscount ?? ''}
                          onChange={(e) => handleInputChange(index, 'rateDiscount', e.target.value)}
                          inputProps={{ step: 0.01, min: 0, max: 6 }}
                          variant="outlined"
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <TextField
                          fullWidth
                          size="small"
                          type="text"
                          value={formatNumberWithCommas(formData.transactionFee)}
                          onChange={(e) => handleInputChange(index, 'transactionFee', e.target.value)}
                          variant="outlined"
                          inputProps={{ inputMode: 'numeric', pattern: '[0-9,]*' }}
                        />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-3 mt-4">
          <button
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'กำลังบันทึก...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FutureRateModal;

